import React, { useEffect, useRef, useState } from 'react';

import {
  FolderOpenOutlined,
  QuestionCircleOutlined,
  TranslationOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
} from 'antd';
import type { InputRef } from 'antd';
import { message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import type { RcFile } from 'antd/es/upload/interface';
import { useTranslation } from 'react-i18next';

import { icons } from 'components/DynamicIcon/DynamicIcon';

import ReportFileUploader, {
  CustomReportFileUpload,
} from 'scenes/Settings/NewReportAndMenus/components/ReportFileUploader';

import { MenuNode } from 'models/MenuNode';

import { FileService } from 'services/FileService';
import {
  CreateMenuForCurrentCompanyRequestDto,
  MenuService,
} from 'services/MenuService';

import { REPORT_TYPES_ITEMS } from 'utils/Constants';
import { FileHelper } from 'utils/FileHelper';

import { MenuType } from '../../../../config/Config';

interface NewReportModalProps {
  parentNode: MenuNode | null;
  isOpen: boolean;
  handleModalCancel: () => void;
  handleNewMenuCreated: (newCreatedNode: MenuNode) => void;
}

const REPORT_PDF_MAX_SIZE = 5 * 1024 * 1024; //5 MB
const REPORT_OFFICE_MAX_SIZE = 5 * 1024 * 1024; //5 MB

function NewReportModal({
  parentNode,
  isOpen,
  handleModalCancel,
  handleNewMenuCreated,
}: NewReportModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const titleInputRef = useRef<InputRef>(null);
  const [selectedType, setSelectedType] = useState<
    | MenuType.IFRAME_VIEWER
    | MenuType.POWER_BI_REPORT
    | MenuType.PDF_VIEWER
    | MenuType.OFFICE_VIEWER
  >();
  const [useLocalizedTitles, setUseLocalizedTitles] = useState(false);

  const [pdfUploadFile, setPdfUploadFile] =
    useState<CustomReportFileUpload | null>(null);
  const [officeUploadFile, setOfficeUploadFile] =
    useState<CustomReportFileUpload | null>(null);

  const formattedIcons = Object.entries(icons)
    .map(([key, value]) => ({
      value: key,
      label: key,
      icon: value,
    }))
    .map((item) => ({
      value: item.value,
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {item.icon && <span style={{ marginRight: 8 }}>{item.icon}</span>}
          {item.label}
        </div>
      ),
    }));

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setSelectedType(undefined);
      setUseLocalizedTitles(false);
      // Enfocar y seleccionar el input de título al abrir
      setTimeout(() => {
        titleInputRef.current?.focus();
        titleInputRef.current?.select?.();
      }, 0);
    }
  }, [isOpen]);

  useEffect(() => {
    form.setFieldValue('showFilterPane', true);
    form.setFieldValue('showPageNavigation', true);
    form.setFieldValue('link', '');
    form.setFieldValue('reportId', '');
    form.setFieldValue('groupId', '');
    form.setFieldValue('configureFallback', false);
    form.setFieldValue('fallbackUrl', '');
    setPdfUploadFile(null);
    setOfficeUploadFile(null);

    // Solo resetear isEncrypted si aún no ha sido establecido
    if (form.getFieldValue('isEncrypted') === undefined) {
      form.setFieldValue('isEncrypted', false);
    }
  }, [selectedType]);

  const onFinish = async (values: any) => {
    if (!selectedType) return;
    setLoading(true);

    try {
      const localizedTitles = useLocalizedTitles
        ? {
            es: (values.localizedTitleEs || '').trim() || null,
            en: (values.localizedTitleEn || '').trim() || null,
          }
        : null;

      const uploadFileIfNeeded = async (): Promise<{
        s3Key: string;
        size: number;
      } | null> => {
        if (
          selectedType === MenuType.PDF_VIEWER &&
          pdfUploadFile?.file?.originFileObj
        ) {
          const rc = pdfUploadFile.file.originFileObj as RcFile;
          const resp = await FileService.createTemporalFile({
            file: rc,
            name: pdfUploadFile.name,
            maxSizeInBytes: REPORT_PDF_MAX_SIZE,
            contentTypesAllowed: ['application/pdf'],
          });
          if (!resp.success) {
            const err = resp.data as any;
            throw new Error(err?.message || 'Error al subir archivo PDF');
          }
          return { s3Key: (resp.data as any).s3Key as string, size: rc.size };
        }
        if (
          selectedType === MenuType.OFFICE_VIEWER &&
          officeUploadFile?.file?.originFileObj
        ) {
          const rc = officeUploadFile.file.originFileObj as RcFile;
          const resp = await FileService.createTemporalFile({
            file: rc,
            name: officeUploadFile.name,
            maxSizeInBytes: REPORT_OFFICE_MAX_SIZE,
            contentTypesAllowed: [
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/vnd.ms-excel',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.ms-powerpoint',
              'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            ],
          });
          if (!resp.success) {
            const err = resp.data as any;
            throw new Error(err?.message || 'Error al subir archivo de Office');
          }
          return { s3Key: (resp.data as any).s3Key as string, size: rc.size };
        }
        return null;
      };

      const uploaded = await uploadFileIfNeeded();

      const additionalConfig: any =
        selectedType === MenuType.POWER_BI_REPORT
          ? {
              settings: {
                showFilterPane: !!values.showFilterPane,
                showPageNavigation: !!values.showPageNavigation,
              },
              groupId: values.groupId,
              reportId: values.reportId,
              fallbackUrl: form.getFieldValue('configureFallback')
                ? (form.getFieldValue('fallbackUrl') || '').trim()
                : null,
            }
          : selectedType === MenuType.PDF_VIEWER ||
            selectedType === MenuType.OFFICE_VIEWER
          ? { file: uploaded?.s3Key, size: uploaded?.size }
          : {};

      additionalConfig.isPasswordProtected = !!values.isEncrypted;

      const body: CreateMenuForCurrentCompanyRequestDto = {
        title: values.title.trim(),
        localizedTitles,
        menuType: selectedType,
        redirectLink:
          selectedType === MenuType.IFRAME_VIEWER
            ? (values.link || '').trim()
            : null,
        icon:
          values.icon === undefined || values.icon === null
            ? null
            : (values.icon.value as string) || null,
        parentMenuCode: parentNode?.code || null,
        additionalConfig,
      };

      const response = await MenuService.createForCurrentCompany(body);
      if (response.success) {
        const { newMenuNode } = response.data as { newMenuNode: MenuNode };
        message.success(
          t(
            'scenes.new_report_and_menus.components.new_report_modal.success.created'
          ),
          3.5
        );
        handleNewMenuCreated(newMenuNode);
        handleModalCancel();
      } else {
        const err = response.data as any;
        message.error(err?.message || 'Error al crear el reporte', 3.5);
      }
    } catch (error: any) {
      message.error(
        error?.message || 'Ocurrió un error inesperado al crear el reporte',
        3.5
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('scenes.new_report_and_menus.components.new_report_modal.title')}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={480}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24} style={{ marginBottom: '18px' }}>
          <Col xs={24}>
            <Alert
              message={t(
                'scenes.new_report_and_menus.components.new_report_modal.alert_added_to_parent'
              )}
              type="info"
              showIcon
            />
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              label={t(
                'scenes.new_report_and_menus.components.new_report_modal.labels.parent_container'
              )}
            >
              <Input
                addonBefore={<FolderOpenOutlined />}
                type="text"
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_report_modal.placeholders.parent_container'
                )}
                readOnly
                defaultValue={
                  parentNode ? parentNode.title : `${window.location.origin}`
                }
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Se elimina encabezado de sección “General” para compactar el diseño */}

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="title"
              label={t(
                'scenes.new_report_and_menus.components.new_report_modal.labels.title'
              )}
              extra={t(
                'scenes.new_report_and_menus.components.new_report_modal.hints.title_fallback'
              )}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.new_report_and_menus.components.new_report_modal.validations.title.required'
                  ),
                },
              ]}
            >
              <Input
                ref={titleInputRef}
                type="text"
                disabled={loading}
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_report_modal.placeholders.title'
                )}
                maxLength={80}
                suffix={
                  <Tooltip
                    title={t(
                      'scenes.new_report_and_menus.components.new_report_modal.tooltips.add_translations'
                    )}
                  >
                    <TranslationOutlined
                      onClick={() => {
                        const next = !useLocalizedTitles;
                        setUseLocalizedTitles(next);
                        if (next) {
                          const base = (
                            form.getFieldValue('title') || ''
                          ).trim();
                          form.setFieldsValue({
                            localizedTitleEs: base,
                            localizedTitleEn: base,
                          });
                        } else {
                          form.setFieldsValue({
                            localizedTitleEs: '',
                            localizedTitleEn: '',
                          });
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        color: useLocalizedTitles ? '#1677ff' : undefined,
                      }}
                    />
                  </Tooltip>
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {useLocalizedTitles && (
          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <TranslationOutlined />
              <span>
                {t(
                  'scenes.new_report_and_menus.components.new_report_modal.sections.translations'
                )}
              </span>
            </div>
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="localizedTitleEn"
                  label={t(
                    'scenes.new_report_and_menus.components.new_report_modal.labels.title_en'
                  )}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_report_modal.placeholders.title_en'
                    )}
                    maxLength={80}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="localizedTitleEs"
                  label={t(
                    'scenes.new_report_and_menus.components.new_report_modal.labels.title_es'
                  )}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_report_modal.placeholders.title_es'
                    )}
                    maxLength={80}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="type"
              label={t(
                'scenes.new_report_and_menus.components.new_report_modal.labels.type'
              )}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.new_report_and_menus.components.new_report_modal.validations.type.required'
                  ),
                },
              ]}
            >
              <Select
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_report_modal.placeholders.select_option'
                )}
                options={REPORT_TYPES_ITEMS}
                allowClear={true}
                onChange={(newValue) => {
                  setSelectedType(newValue);
                }}
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        {selectedType === MenuType.IFRAME_VIEWER && (
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="link"
                label={t(
                  'scenes.new_report_and_menus.components.new_report_modal.labels.link'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.new_report_and_menus.components.new_report_modal.validations.link.required'
                    ),
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const urlRegex =
                        /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w-.]*)*(\?.*)?(#.*)?$/;
                      if (!urlRegex.test(value)) {
                        return Promise.reject(
                          new Error(
                            t(
                              'scenes.new_report_and_menus.components.new_report_modal.validations.link.invalid_url'
                            )
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextArea
                  placeholder={t(
                    'scenes.new_report_and_menus.components.new_report_modal.placeholders.link'
                  )}
                  rows={4}
                  disabled={loading}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {selectedType === MenuType.POWER_BI_REPORT && (
          <>
            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="reportId"
                  label={t(
                    'scenes.new_report_and_menus.components.new_report_modal.labels.report_id'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.new_report_and_menus.components.new_report_modal.validations.report_id.required'
                      ),
                    },
                  ]}
                >
                  <Input
                    type="text"
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_report_modal.placeholders.report_id'
                    )}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="groupId"
                  label={t(
                    'scenes.new_report_and_menus.components.new_report_modal.labels.group_id'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.new_report_and_menus.components.new_report_modal.validations.group_id.required'
                      ),
                    },
                  ]}
                >
                  <Input
                    type="text"
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_report_modal.placeholders.group_id'
                    )}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Bloque de URL de fallback con checkbox integrado en la etiqueta */}
            <Form.Item noStyle shouldUpdate>
              {() => (
                <Row gutter={24}>
                  <Col xs={24} lg={24}>
                    <Form.Item
                      name="fallbackUrl"
                      label={
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <Checkbox
                            checked={!!form.getFieldValue('configureFallback')}
                            disabled={loading}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              form.setFieldValue('configureFallback', checked);
                              if (!checked) {
                                form.setFieldValue('fallbackUrl', '');
                              }
                            }}
                          />
                          <span
                            onClick={() => {
                              if (loading) return;
                              const next =
                                !form.getFieldValue('configureFallback');
                              form.setFieldValue('configureFallback', next);
                              if (!next) {
                                form.setFieldValue('fallbackUrl', '');
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {t(
                              'scenes.new_report_and_menus.components.new_report_modal.labels.fallback_url'
                            )}
                          </span>
                          <Tooltip
                            title={t(
                              'scenes.new_report_and_menus.components.new_report_modal.tooltips.fallback_url'
                            )}
                          >
                            <QuestionCircleOutlined
                              style={{
                                marginLeft: 4,
                                verticalAlign: 'middle',
                                transform: 'translateY(1px)',
                              }}
                            />
                          </Tooltip>
                        </div>
                      }
                      rules={[
                        {
                          validator: (_, value) => {
                            const checked =
                              !!form.getFieldValue('configureFallback');
                            if (!checked) return Promise.resolve();
                            if (!value) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'scenes.new_report_and_menus.components.new_report_modal.validations.fallback_url.required'
                                  )
                                )
                              );
                            }
                            const urlRegex =
                              /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w-.]*)*(\?.*)?(#.*)?$/;
                            if (!urlRegex.test(value)) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'scenes.new_report_and_menus.components.new_report_modal.validations.fallback_url.invalid_url'
                                  )
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <TextArea
                        rows={4}
                        placeholder={t(
                          'scenes.new_report_and_menus.components.new_report_modal.placeholders.fallback_url'
                        )}
                        disabled={
                          !form.getFieldValue('configureFallback') || loading
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Form.Item>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="showFilterPane"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox disabled={loading}>
                    {t(
                      'scenes.new_report_and_menus.components.new_report_modal.checkbox.show_filter_pane'
                    )}
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="showPageNavigation"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox disabled={loading}>
                    {t(
                      'scenes.new_report_and_menus.components.new_report_modal.checkbox.show_page_navigation'
                    )}
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {selectedType === MenuType.PDF_VIEWER && (
          <>
            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="pdfFile"
                  label={t(
                    'scenes.new_report_and_menus.components.new_report_modal.labels.file'
                  )}
                  rules={[
                    {
                      required: true,
                      validator: () => {
                        if (pdfUploadFile) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            t(
                              'scenes.new_report_and_menus.components.new_report_modal.validations.pdf_file.required'
                            )
                          )
                        );
                      },
                    },
                  ]}
                >
                  <ReportFileUploader
                    renameFile
                    allowedTypes={['application/pdf']}
                    allowedTypeErrorMessage={t(
                      'scenes.new_report_and_menus.components.new_report_modal.file_upload.pdf.allowed_type_error'
                    )}
                    helpFileText={t(
                      'scenes.new_report_and_menus.components.new_report_modal.file_upload.pdf.help_text',
                      {
                        size: FileHelper.formatFileSize(
                          REPORT_PDF_MAX_SIZE,
                          true
                        ),
                      }
                    )}
                    maxSizeInBytes={REPORT_PDF_MAX_SIZE}
                    uploadedFile={pdfUploadFile}
                    setUploadFile={setPdfUploadFile}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {selectedType === MenuType.OFFICE_VIEWER && (
          <>
            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="officeFile"
                  label={t(
                    'scenes.new_report_and_menus.components.new_report_modal.labels.file'
                  )}
                  rules={[
                    {
                      required: true,
                      validator: () => {
                        if (officeUploadFile) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            t(
                              'scenes.new_report_and_menus.components.new_report_modal.validations.office_file.required'
                            )
                          )
                        );
                      },
                    },
                  ]}
                >
                  <ReportFileUploader
                    renameFile
                    allowedTypes={[
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      'application/vnd.ms-excel',
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'application/vnd.ms-powerpoint',
                      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    ]}
                    allowedTypeErrorMessage={t(
                      'scenes.new_report_and_menus.components.new_report_modal.file_upload.office.allowed_type_error'
                    )}
                    helpFileText={t(
                      'scenes.new_report_and_menus.components.new_report_modal.file_upload.office.help_text',
                      {
                        size: FileHelper.formatFileSize(
                          REPORT_OFFICE_MAX_SIZE,
                          true
                        ),
                      }
                    )}
                    maxSizeInBytes={REPORT_OFFICE_MAX_SIZE}
                    uploadedFile={officeUploadFile}
                    setUploadFile={setOfficeUploadFile}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {selectedType && (
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item name="isEncrypted" valuePropName="checked">
                <Checkbox disabled={loading}>
                  {t(
                    'scenes.new_report_and_menus.components.new_report_modal.checkbox.is_encrypted'
                  )}
                  <Tooltip
                    title={t(
                      'scenes.new_report_and_menus.components.new_report_modal.tooltips.report_encryption'
                    )}
                  >
                    <QuestionCircleOutlined
                      style={{
                        marginLeft: 4,
                        marginBottom: 4,
                        verticalAlign: 'middle',
                        transform: 'translateY(1px)',
                      }}
                    />
                  </Tooltip>
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Selector de ícono: opcional y siempre visible al final */}
        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="icon"
              label={t(
                'scenes.new_report_and_menus.components.new_container_modal.labels.icon'
              )}
            >
              <Select
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_report_modal.placeholders.select_option'
                )}
                allowClear
                labelInValue
                options={formattedIcons}
                showSearch
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(
                'scenes.new_report_and_menus.components.new_report_modal.buttons.save_changes'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default NewReportModal;
